import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import { DataIn, DataOut, DoorControlType } from './inner-range-inception.interface';

@Component({
  tag: 'lar-inner-range-inception-door',
  styleUrl: 'inner-range-inception-door.scss',
  shadow: true
})
export class InnerRangeDoor {
  @Element() el!: HTMLLarInnerRangeDoorElement;
  @Prop() doorType?: string;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'door';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * Component specific options (see above)
   */
  @Prop() options?: any;
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter<DataOut>;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() alarm: boolean = false;
  @State() loading: boolean = true;
  @State() locked: boolean = false;
  @State() timedUnlockTimeSecs: number = 0;
  @State() inceptionError: boolean = false;
  private node!: HTMLLarNodeElement;

  /**
   * Larva input message
   */
  @Method()
  async input(data: DataIn) {
    this.loading = false;
    this.locked = data && data.locked;
    this.doorType = data && data.mode || 'toggle';
    this.timedUnlockTimeSecs = (data && data.timedUnlockTimeSecs) || 0;
    this.inceptionError = data && (data.connencting || data.error);
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit();
  }

  handleChange() {
    this.loading = true;
    let doorControlType: DoorControlType = this.locked ? 'Unlock' : 'Lock';
    if (this.doorType === 'toggle') {
      doorControlType = 'ToggleLock';
    }
    if (this.doorType === 'toggle' && this.timedUnlockTimeSecs > 0) {
      doorControlType = 'TimedUnlock';
    }
    return this.output.emit({ 
      doorControlType,
      doorControlData: {
        ...this.doorType === 'toggle' && this.timedUnlockTimeSecs > 0 ? { TimeSecs: this.timedUnlockTimeSecs } : {}
      }
    });
  }

  render() {
    const iconSmall = this.inceptionError ? 'warning' : this.locked ? 'lock' : 'unlock';
    const value = this.doorType === 'toggle' && this.timedUnlockTimeSecs > 0 ? 'door.buzzOpen' : this.doorType === 'toggle' ? 'door.toggleLockUnlock' : this.locked ? 'door.unlock' : 'door.lock';
    const colorIconSmall = this.allowIndicationAutoColoring && !this.loading ? (!this.locked || !this.inceptionError? 'danger': 'success') : this.colorIconSmall;
    const componentProps = {
      onClick: () => this.handleChange(),
      value,
      icon: this.locked ? 'lock' : 'unlock',
      disabled: this.loading,
      color: this.colorInputs || this.color
    };
    return (
      <lar-node
        value={this.inceptionError ? 'door.error' : this.locked ? 'door.locked' : 'door.unlocked'}
        hideTitles={this.hideTitles}
        iconSmall={iconSmall}
        colorIconSmall={colorIconSmall}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        nodeSize={this.nodeSize}
        log={this.log}
        loading={this.loading}
        component="lar-inner-range-inception-door-content"
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    );
  }
}
